import { TFunction } from "i18next";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import Input from "../../components/Form/Input/input";
import { Select } from "../../components/Form/Input/select";
import Label from "../../components/Form/Label";
import { FieldHint, Form, FormGroup } from "../../components/Form/styles";
import { KeepoLoader } from "../../components/KeepoLoader";
import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { BANKS } from "../../model/banks";
import { isApiError } from "../../services/api/errors";
import {
  BankInput,
  changeBankAccount,
  getBankAccount,
  paymentErrorFields,
  PaymentsState,
} from "../../services/payments";
import { onlyDigits } from "../../utils/brDocument";
import { BankSelect } from "./BankSelect";

const formPrefix = "pages.settings.payments.form";
const prefix = "pages.settings.payments.bankModal";

/** The bank account as typed (the registration's and the bank change's). */
export type BankValues = {
  bankCode: string;
  branch: string;
  branchDigit: string;
  account: string;
  accountDigit: string;
  accountType: "checking" | "savings";
};

export const EMPTY_BANK: BankValues = {
  bankCode: "",
  branch: "",
  branchDigit: "",
  account: "",
  accountDigit: "",
  accountType: "checking",
};

/** Each bank field's key in the API's errors (dotted, as the request body). */
export const BANK_API_KEYS: Record<keyof BankValues, string> = {
  bankCode: "bank.code",
  branch: "bank.branch",
  branchDigit: "bank.branchDigit",
  account: "bank.account",
  accountDigit: "bank.accountDigit",
  accountType: "bank.type",
};

/** What's wrong with each bank field, as translation keys under `form.errors`. */
export const validateBank = (values: BankValues): Partial<Record<keyof BankValues, string>> => {
  const errors: Partial<Record<keyof BankValues, string>> = {};
  if (!BANKS.some(bank => bank.code === values.bankCode)) errors.bankCode = "bank";
  if (!/^\d{1,5}$/.test(values.branch)) errors.branch = "branch";
  if (values.branchDigit && !/^[0-9xX]$/.test(values.branchDigit)) errors.branchDigit = "branchDigit";
  if (!/^\d{1,13}$/.test(values.account)) errors.account = "account";
  if (!/^[0-9xX]{1,2}$/.test(values.accountDigit)) errors.accountDigit = "accountDigit";
  return errors;
};

export const toBankInput = (values: BankValues): BankInput => ({
  code: values.bankCode,
  name: BANKS.find(bank => bank.code === values.bankCode)?.name ?? "",
  branch: values.branch,
  branchDigit: values.branchDigit,
  account: values.account,
  accountDigit: values.accountDigit,
  type: values.accountType,
});

/** A field and, under it, what's wrong with it (named, so the error is found with its field). */
export const Field = ({ label, error, children }: { label: string; error?: string; children: ReactNode }) => (
  <FormGroup role="group" aria-label={label}>
    {children}
    {error && <FieldError>{error}</FieldError>}
  </FormGroup>
);

type TextFieldProps = {
  id: string;
  label: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  format?: (value: string) => string;
  type?: string;
  max?: number;
  placeholder?: string;
  prefix?: string;
  /** On the right, inside the field (e.g. a link). */
  suffix?: ReactNode;
};

export const TextField = ({
  id,
  label,
  value,
  error,
  onChange,
  format,
  type,
  max,
  placeholder,
  prefix,
  suffix,
}: TextFieldProps) => (
  <Field label={label} error={error}>
    <Input
      id={id}
      type={type || "text"}
      label={label}
      value={value}
      maxLength={max}
      ignoreLengthInfo
      placeholder={placeholder}
      prefix={prefix}
      sufix={suffix}
      invalid={!!error}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        onChange(format ? format(event.target.value) : event.target.value)
      }
    />
  </Field>
);

type SelectFieldProps = {
  id: string;
  label: string;
  value: string;
  error?: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
};

export const SelectField = ({ id, label, value, error, options, onChange }: SelectFieldProps) => (
  <Field label={label} error={error}>
    <Select
      id={id}
      value={value}
      aria-invalid={error ? true : undefined}
      onChange={event => onChange(event.target.value)}>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </Select>
    <Label htmlFor={id} text={label} />
  </Field>
);

const digits = (max: number) => (value: string) => onlyDigits(value).slice(0, max);
const checkDigit = (max: number) => (value: string) => value.replace(/[^0-9xX]/g, "").slice(0, max);

type BankFieldsProps = {
  /** Prefix of the inputs' ids (unique on the screen) */
  idPrefix: string;
  values: BankValues;
  /** The error of each field, by its API key (bank.code, bank.account…) */
  errors: Record<string, string>;
  onChange: (key: keyof BankValues, value: string) => void;
};

/** The bank account's fields, in the registration's same CPF (the note says so). */
export const BankFields = ({ idPrefix, values, errors, onChange }: BankFieldsProps) => {
  const { t } = useTranslation();

  const text = (key: keyof BankValues, format: (value: string) => string) => (
    <TextField
      id={`${idPrefix}-${key}`}
      label={t(`${formPrefix}.${key}`)}
      value={values[key]}
      error={errors[BANK_API_KEYS[key]]}
      format={format}
      onChange={value => onChange(key, value)}
    />
  );

  const select = (key: keyof BankValues, options: { value: string; label: string }[]) => (
    <SelectField
      id={`${idPrefix}-${key}`}
      label={t(`${formPrefix}.${key}`)}
      value={values[key]}
      error={errors[BANK_API_KEYS[key]]}
      options={options}
      onChange={value => onChange(key, value)}
    />
  );

  return (
    <>
      <Note>{t(`${formPrefix}.sameDocument`)}</Note>
      {/* a list with a search: the banks are many */}
      <Field label={t(`${formPrefix}.bankCode`)} error={errors[BANK_API_KEYS.bankCode]}>
        <BankSelect
          id={`${idPrefix}-bankCode`}
          label={t(`${formPrefix}.bankCode`)}
          value={values.bankCode}
          invalid={!!errors[BANK_API_KEYS.bankCode]}
          onChange={value => onChange("bankCode", value)}
        />
        <Label htmlFor={`${idPrefix}-bankCode`} text={t(`${formPrefix}.bankCode`)} />
      </Field>
      <Pair>
        {text("branch", digits(5))}
        {text("branchDigit", checkDigit(1))}
      </Pair>
      <Pair>
        {text("account", digits(13))}
        {text("accountDigit", checkDigit(2))}
      </Pair>
      {select("accountType", [
        { value: "checking", label: t(`${formPrefix}.checking`) },
        { value: "savings", label: t(`${formPrefix}.savings`) },
      ])}
    </>
  );
};

/** The marker the API puts under a field of ours that Pagar.me refused (its own text is in English). */
const CHECK_FIELD = "CHECK_FIELD";

/**
 * The API's field errors, split into those under the form's fields ($fieldKeys, by API key) and those at the top.
 * Our own validation (INVALID_INPUT) brings texts, shown as they come. A refusal by Pagar.me (PROVIDER_REJECTED) is
 * shown in the person's language, whatever the API wrote: "Confira este campo." under a field, and the general
 * refusal at the top for anything else.
 */
export const apiErrors = (
  error: unknown,
  fieldKeys: Set<string>,
  t: TFunction
): { onFields: Record<string, string>; others: string[] } => {
  const byProvider = isApiError(error, "PROVIDER_REJECTED");
  const onFields: Record<string, string> = {};
  const others = new Set<string>();
  Object.entries(paymentErrorFields(error)).forEach(([key, message]) => {
    // The bank's name comes from the chosen bank
    const field = key === "bank.name" ? "bank.code" : key;
    if (fieldKeys.has(field)) {
      onFields[field] = byProvider || message === CHECK_FIELD ? t(`${formPrefix}.errors.checkField`) : String(message);
    } else if (byProvider || key === "provider") {
      others.add(t(`${formPrefix}.errors.providerRejected`));
    } else {
      others.add(String(message));
    }
  });
  return { onFields, others: [...others] };
};

const FIELD_KEYS = new Set(Object.values(BANK_API_KEYS));

type Props = {
  siteId: string;
  /** Saved: the screen's new state (the card shows the new account). */
  onDone: (state: PaymentsState) => void;
  onClose: () => void;
  /** The other Keepos of the registration (names), told that the new account is theirs too. */
  sharedWith?: string;
};

/** Trocar conta bancária: a new account for the registration of this Keepo, in the same CPF. */
export const BankAccountModal = ({ siteId, onDone, onClose, sharedWith }: Props) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<BankValues>(EMPTY_BANK);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();
  const [isSaving, setSaving] = useState(false);
  // The current account, from Pagar.me, fills the fields; until then a loader, and if it can't, empty with a note
  const [loading, setLoading] = useState<"loading" | "done" | "failed">("loading");
  // The account as the provider has it: Salvar with nothing changed has nothing to send
  const [current, setCurrent] = useState<BankValues>();

  useEffect(() => {
    let active = true;
    getBankAccount(siteId)
      .then(account => {
        if (!active) return;
        const loaded: BankValues = {
          bankCode: account.code,
          branch: account.branch,
          branchDigit: account.branchDigit,
          account: account.account,
          accountDigit: account.accountDigit,
          accountType: account.type === "savings" ? "savings" : "checking",
        };
        setCurrent(loaded);
        setValues(typed => ({
          bankCode: typed.bankCode || loaded.bankCode,
          branch: typed.branch || loaded.branch,
          branchDigit: typed.branchDigit || loaded.branchDigit,
          account: typed.account || loaded.account,
          accountDigit: typed.accountDigit || loaded.accountDigit,
          accountType: loaded.accountType,
        }));
        setLoading("done");
      })
      .catch(() => active && setLoading("failed"));
    return () => {
      active = false;
    };
  }, [siteId]);

  const set = (key: keyof BankValues, value: string) => {
    setValues(current => ({ ...current, [key]: value }));
    setErrors(current => {
      const next = { ...current };
      delete next[BANK_API_KEYS[key]];
      return next;
    });
  };

  const save = async () => {
    setFormError(undefined);
    // The account the provider already has: nothing to change, nothing sent (a change can put the registration back
    // under review, or ask for support)
    if (current && (Object.keys(current) as (keyof BankValues)[]).every(key => values[key] === current[key])) {
      onClose();
      return;
    }
    const found = Object.entries(validateBank(values)) as [keyof BankValues, string][];
    setErrors(
      Object.fromEntries(found.map(([key, error]) => [BANK_API_KEYS[key], t(`${formPrefix}.errors.${error}`)]))
    );
    if (found.length > 0) return;

    setSaving(true);
    try {
      onDone(await changeBankAccount(siteId, toBankInput(values)));
    } catch (error) {
      setSaving(false);
      if (isApiError(error, "PROVIDER_UNAVAILABLE")) {
        setFormError(t(`${prefix}.errors.unavailable`));
        return;
      }
      // Pagar.me asks for its own second check to change the account: only support can do it
      if (isApiError(error, "BANK_CHANGE_REQUIRES_SUPPORT")) {
        setFormError(t("pages.settings.payments.bankBySupport"));
        return;
      }
      // A registration never sent (draft) has no account at the provider to change
      if (isApiError(error, "NOT_FOUND")) {
        setFormError(t(`${prefix}.errors.notFound`));
        return;
      }
      const { onFields, others } = apiErrors(error, FIELD_KEYS, t);
      setErrors(onFields);
      if (others.length > 0) setFormError(others.join(" "));
      else if (Object.keys(onFields).length === 0) setFormError(t(`${prefix}.errors.generic`));
    }
  };

  return (
    <Modal
      title={t(`${prefix}.title`)}
      escapeExit
      busy={isSaving}
      onExit={onClose}
      error={formError}
      footer={
        <>
          <ModalSecondaryButton type="button" disabled={isSaving} onClick={onClose}>
            {t(`${formPrefix}.cancel`)}
          </ModalSecondaryButton>
          {/* not before the current account is in the fields: it would check the empty ones, still hidden */}
          <ModalButton loading={isSaving} disabled={loading === "loading"} onClick={save}>
            {t(`${prefix}.save`)}
          </ModalButton>
        </>
      }>
      <Form
        noValidate
        onSubmit={event => {
          event.preventDefault();
          if (!isSaving) save();
        }}>
        <Fields>
          {sharedWith && <SharedNote>{t(`${prefix}.sharedWith`, { keepos: sharedWith })}</SharedNote>}
          {loading === "loading" ? (
            <Loading role="status" aria-label={t(`${prefix}.loading`)}>
              <KeepoLoader size={24} color="var(--color-neutral-white)" />
            </Loading>
          ) : (
            <>
              {loading === "failed" && <SharedNote>{t(`${prefix}.loadError`)}</SharedNote>}
              <BankFields idPrefix="bank-change" values={values} errors={errors} onChange={set} />
            </>
          )}
        </Fields>
      </Form>
    </Modal>
  );
};

const FieldError = styled(FieldHint)`
  color: var(--color-feedback-danger);
`;

/* Two short fields side by side; stacked on narrow phones */
export const Pair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  /* the fields stay level when only one of them shows an error under it */
  align-items: start;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${FormGroup} {
    margin-bottom: 0;
  }
`;

/* The other Keepos the new account also pays out to */
const SharedNote = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 0;
`;
